/**
 * CardDetailPage — 카드 상세 · 시세 (PKA 화면정의서 SC-007).
 *
 * 표시 규칙은 PKA_PRD_v0.1 §5 "가격 데이터 기준" 을 따른다:
 *   §5.0  언어판(한국판·영문판·일본판)은 **합치지 않는다** — 카드 1행이 곧 인쇄판이다
 *   §5.4  감정사·점수가 다르면 별개 가격 → 등급 행을 합치지 않는다
 *   §5.4  표본이 얇으면 "최근 거래 사례", 아예 없으면 숫자를 만들지 않는다
 *   §4.2  비회원에게는 "약 N만원대"까지만 — 카드 식별 정보는 전부 공개
 *
 * **이력은 2026-09-04 부터 실제로 온다** (`pr_prc_bydd` 101만 행 · 시세 보유 카드의
 * 92.6%). 그래프·근거 값은 그 데이터를 그린다. 축은 대표가를 만든 그 행과 같아서
 * 그래프의 마지막 점이 위 큰 금액과 정확히 일치한다(BE `findPriceHistory` 참조).
 *
 * ⚠ **여전히 못 그리는 것.** ① 수입 프리미엄 — 현지↔국내 비교 데이터 없음 ② 호가의
 * **팔면(BID)** — 수집 계약의 관측 종류가 ASK|SOLD 뿐이라 구매희망가라는 축 자체가
 * 안 온다. 그래서 design 의 '차이(스프레드)'도 못 낸다 ③ 출처별 건수·실거래 목록 —
 * `pr_prc_obs` 가 0행. 더미로 채우면 화면이 그럴듯해지는 대신 **없는 시세를 보여주게
 * 된다** — 이 앱에서 그건 가장 하면 안 되는 거짓말이다. 자리만 잡고 "준비 중"으로 둔다.
 *
 * ⚠ 관측 종류를 **라벨로 구분한다.** 한국판 카드의 69.4%(2,408장 중 1,670장)는 체결이
 * 아예 없어 대표가·이력이 전부 호가다(en 2.1% · ja 10.6%). 전부 "거래"라고 부르면
 * 대부분의 한국 카드에서 거짓말이 된다 — `history.observationKind` 로 라벨을 고른다.
 * SOLD 조차 실결제금액이 아니라 **판매완료 시점의 게시가격**이라(DB 컬럼 주석)
 * "실거래가" 라는 말은 쓰지 않는다.
 *
 * `PriceWithOrigin` 은 아직 아무 데서도 안 쓰인다. **죽은 코드가 아니라 대기 중인
 * 코드다** — 환율이 들어오면 §5.7 원본통화 병기에 그대로 쓴다. 정리한다고 지우지 말 것.
 *
 * 기획확정안 §6 유지: 포트폴리오/컬렉션을 분리하지 않으므로 저장 액션은 '컬렉션에 추가' 하나다.
 *
 * 배치 (2026-08-05 design UX 리뷰 — 실 API 이식): 좌/우로 **질문을 나눈다**.
 *   좌측 "이 카드는 무엇인가"  이미지·이름 · 담기(1단에서는 하단 고정) · 상세정보(접힘)
 *   우측 "얼마인가"            시세 · 등급별 · (준비 중) 이력·프리미엄·호가
 * 1단(모바일)에서는 CSS order 로 "값 → 근거 → 행동" 순서를 다시 잡는다
 * (CardDetailPage.module.css 참조). 카드명·공유는 앱바로 승격(AppBarSlot).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { track } from "../../lib/tracking";
import { useTranslation } from "react-i18next";
import { Badge } from "@astryxdesign/core/Badge";
import { Button } from "@astryxdesign/core/Button";
import { Card } from "@astryxdesign/core/Card";
import { Collapsible } from "@astryxdesign/core/Collapsible";
import { Divider } from "@astryxdesign/core/Divider";
import { Grid } from "@astryxdesign/core/Grid";
import { Heading } from "@astryxdesign/core/Heading";
import { AspectRatio } from "@astryxdesign/core/AspectRatio";
import { Link } from "@astryxdesign/core/Link";
import Icon from "../../components/primitives/Icon";
import PriceChart from "../../components/primitives/PriceChart";
import Notice from "../../components/feedback/Notice";
import AddToCollectionSheet from "../../components/collection/AddToCollectionSheet";
import ConfidenceSheet from "../../components/card/ConfidenceSheet";
import AppInstallBanner from "../../components/web/AppInstallBanner";
import { MetadataList, MetadataListItem } from "@astryxdesign/core/MetadataList";
import { Section } from "@astryxdesign/core/Section";
import { SegmentedControl, SegmentedControlItem } from "@astryxdesign/core/SegmentedControl";
import { IconButton } from "@astryxdesign/core/IconButton";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import { Table, proportional } from "@astryxdesign/core/Table";
import { Text } from "@astryxdesign/core/Text";
import { Token } from "@astryxdesign/core/Token";
import { VStack, HStack } from "@astryxdesign/core/Layout";
import { useAppBarSlot } from "../../components/layout/AppBarSlotContext";
import {
  cardImageUrl,
  recordCardView,
  HISTORY_PERIODS,
  type HistoryPeriod,
  type PriceView,
  type VariantPrice,
} from "../../api/card";
import { useCardDetailQuery, useCardPriceHistoryQuery } from "../../hooks/useCardQueries";
import { useAuthStore } from "../../store/authStore";
import { useActivityNavigator } from "../../navigation/useActivityNavigator";
import { useRequireAuth } from "../../navigation/useRequireAuth";
import { maskPrice } from "../../lib/priceMasking";
import { formatKrw } from "../../lib/formatPrice";
import {
  consumePendingCardAction,
  setPendingCardAction,
} from "../../navigation/pendingCardAction";
import styles from "./CardDetailPage.module.css";

/**
 * 갱신 24시간 초과 = 오래된 시세 (SC-007 · FN-016, design 2026-08-28).
 *
 * ⚠ 판정을 **화면에서** 한다. 서버가 "몇 시간 전"을 굳혀 내려보내면 응답이 캐시되는
 *   순간 그 값이 늙지 않는다 — 서버는 시각(refreshedAt)만 주고 나이는 여기서 잰다.
 * ⚠ 구 서버는 refreshedAt 을 안 보낸다. 그때는 아무 말도 하지 않는다(fail-open) —
 *   모르는 것을 '오래됐다'로 말하면 멀쩡한 시세가 경고를 달고 뜬다.
 */
const STALE_QUOTE_HOURS = 24;

function isStaleQuote(refreshedAt: string | null | undefined): boolean {
  if (!refreshedAt) return false;
  const ms = Date.parse(refreshedAt);
  if (Number.isNaN(ms)) return false;
  return Date.now() - ms >= STALE_QUOTE_HOURS * 60 * 60 * 1000;
}

/** 신뢰도 코드(BE `RELI_COMM_CD`) → Badge 변형. 미지의 코드는 중립으로 떨어뜨린다. */
const RELIABILITY_BADGE: Record<string, "success" | "warning" | "neutral"> = {
  HIGH: "success",
  MEDIUM: "warning",
  LOW: "neutral",
};

/**
 * 표면(FINISH) 코드 → 표시 문구.
 *
 * ⚠ 실 데이터의 `FINSH_CD` 는 **수집서버 원문 그대로**다 — 로컬 실측 13종
 *   (`DEFAULT`·`Holofoil`·`holofoil`·`reverseHolofoil`·`1stEdition`·`1st Edition`…),
 *   대소문자·띄어쓰기까지 갈린다. 그래서 키를 그대로 i18n 에 넣을 수 없다:
 *   영문자·숫자만 남겨 대문자로 눌러 맞춘 뒤에 찾는다.
 * ⚠ 못 찾으면 **원문을 그대로 적는다.** 13종에 한국어 이름을 지어 붙이는 것은
 *   번역이 아니라 창작이고, 그 자리는 원천 정규화(importer)가 할 일이다.
 */
const FINISH_KEY: Record<string, string> = {
  DEFAULT: "NORMAL",
  NORMAL: "NORMAL",
  HOLOFOIL: "HOLOFOIL",
  HOLO: "HOLOFOIL",
  REVERSEHOLOFOIL: "REVERSE_HOLO",
};

function finishLabel(t: (key: string) => string, finish: string): string {
  const key = FINISH_KEY[finish.replace(/[^a-zA-Z0-9]/g, "").toUpperCase()];
  return key ? t(`set.finish.${key}`) : finish;
}

/** `2026-08-05` → `2026.08.05`. 값이 없으면 대시. */
function formatDotDate(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10).replace(/-/g, ".") : "-";
}

/**
 * 금액 표시.
 *
 * ⚠ 원본 통화와 원화를 **함께** 적는 §5.7 표기는 아직 못 한다 — 응답이 금액 하나와
 * 그 통화만 주고 환율이 없다. 지어낸 환산가를 병기하느니 온 통화 그대로 적는다.
 */
function formatAmount(price: PriceView): string {
  const amount = price.amount ?? 0;
  if (!price.currency || price.currency === "KRW") return formatKrw(amount);
  return `${amount.toLocaleString("ko-KR")} ${price.currency}`;
}

/** 등락 표기 — 색만으로 구분하지 않도록 ▲▼ 를 항상 함께 쓴다 (색각 이상 대응). */
function Trend({ value, label }: { value: number; label?: string }) {
  const isUp = value >= 0;
  return (
    <HStack gap={1} align="center">
      {label && <Text type="supporting" color="secondary">{label}</Text>}
      <Text
        type="label"
        weight="semibold"
        style={{ color: isUp ? "var(--price-up)" : "var(--price-down)" }}
      >
        {isUp ? "▲" : "▼"} {Math.abs(value).toFixed(1)}%
      </Text>
    </HStack>
  );
}

/**
 * 아직 데이터가 없는 섹션의 자리.
 *
 * 섹션을 통째로 숨기지 않는 이유: 이 화면에 무엇이 올 예정인지가 기획·디자인·개발이
 * 공유하는 정보이고, 숨겨두면 "빠뜨린 것"과 "아직 없는 것"이 구분되지 않는다.
 */
function PendingSection({
  title,
  badge,
  body,
}: {
  title: string;
  badge: string;
  body: string;
}) {
  return (
    <VStack gap={2}>
      <Heading level={3}>{title}</Heading>
      <Card padding={4}>
        <VStack gap={2} align="start">
          <Badge variant="neutral" label={badge} />
          <Text type="supporting" color="secondary">{body}</Text>
        </VStack>
      </Card>
    </VStack>
  );
}

/**
 * 비회원에게 **잠긴** 섹션의 자리 — 기획확정안 §4.2 "마스킹하고 로그인 버튼을
 * 제공합니다" (IA SC-007 의 간편로그인 유도 → SC-021).
 *
 * ⚠ 위 `PendingSection` 과 **다른 것**이다. 저쪽은 아직 아무에게도 없는 데이터고,
 *   이쪽은 있는데 로그인해야 보이는 데이터다. 둘을 한 자리에 합쳐 두었더니 비회원이
 *   이력·호가·등급별 자리에서 '데이터 준비 중' 을 읽었다 — 로그인하면 바로 나오는
 *   값을 없다고 말한 셈이고(실측: 시세 있는 카드의 82.7% 가 90일 이력을 갖고 있다),
 *   기획이 이 자리에 걸어 둔 가입 유도까지 함께 사라졌다.
 */
function GuestGateSection({
  title,
  body,
  onLogin,
}: {
  title: string;
  body: string;
  onLogin: () => void;
}) {
  const { t } = useTranslation("card");
  return (
    <VStack gap={2}>
      <Heading level={3}>{title}</Heading>
      {/* 배치는 대표가의 비회원 카드와 같다 — 왼쪽에 무엇이 잠겼는지, 오른쪽에 할 일.
          같은 성격의 자리가 화면 안에서 서로 다른 모양이면 두 번 배워야 한다.
          ⚠ 버튼은 secondary — 이 화면의 주 액션은 '컬렉션에 추가' 다. */}
      <Card padding={4}>
        <HStack gap={3} align="center" justify="between" width="100%" wrap="wrap">
          <VStack gap={1} align="start">
            <Badge variant="neutral" label={t("guest.lockedBadge")} />
            <Text type="supporting" color="secondary">{body}</Text>
          </VStack>
          <Button variant="secondary" label={t("guest.login")} clickAction={onLogin} />
        </HStack>
      </Card>
    </VStack>
  );
}

/**
 * @param cardSerialNo ActivityHost 전용 — activity 안에서는 라우트 매치가 없어 useParams
 *   가 비므로 resolver 가 URL 에서 파싱해 넘긴다 (/scan/cards/:id · /cards/:id 공통).
 *   라우트 렌더(웹 딥링크 등)에서는 생략 — useParams.
 */
export default function CardDetailPage({
  cardSerialNo: cardSerialNoProp,
}: { cardSerialNo?: number } = {}) {
  const { t } = useTranslation("card");
  const { cardSerialNo: paramSerialNo } = useParams<{ cardSerialNo: string }>();
  const [searchParams] = useSearchParams();

  // §3.3 마지막 문단: 버전을 끝까지 확정하지 못하면 정확한 시세를 확정하지 않는다.
  const variantUnconfirmed = searchParams.get("varnt") === "UNCONFIRMED";

  // 더미 시절의 문자열 uid 링크(홈·세트 상세)가 아직 남아 있다 — 숫자가 아니면 조회하지 않는다.
  const rawSerialNo =
    cardSerialNoProp !== undefined ? String(cardSerialNoProp) : paramSerialNo;
  const cardSerialNo = rawSerialNo && /^\d+$/.test(rawSerialNo) ? Number(rawSerialNo) : null;
  const detail = useCardDetailQuery(cardSerialNo);

  /**
   * 카드 상세 조회 1건. source 는 **경로로 확실히 갈리는 것만** 쓴다 —
   * 스캔 위에 뜬 결과 오버레이(/scan/cards/:id)와 탐색에서 내려온 상세(/cards/:id).
   * 홈·검색·세트를 더 쪼개려면 호출부가 출처를 넘겨줘야 하는데, 지금 화면들은 그걸
   * 안 넘기므로 추측해서 라벨을 붙이지 않는다(틀린 라벨이 없는 것보다 나쁘다).
   */
  useEffect(() => {
    if (cardSerialNo === null) return;
    track("card_detail_view", {
      cardSerialNo,
      source: window.location.pathname.startsWith("/scan/") ? "scan" : "catalog",
    });
  }, [cardSerialNo]);
  const card = detail.data;

  /**
   * 열람 기록 (FN-048 · SC-007) — 홈 §2 칩의 조회수 축.
   *
   * ⚠ **카드가 바뀔 때만 한 번**이다. detail 쿼리의 성공에 매달면 재조회(포커스 복귀 등)
   *   마다 한 번씩 더 세어 화면을 오래 열어 둔 사람이 인기를 만든다.
   * ⚠ StrictMode 는 개발에서 effect 를 두 번 돌린다 — 그 두 번째는 실제 조회가 아니므로
   *   ref 로 같은 카드의 중복 발사를 막는다. 운영 빌드에서도 무해하다.
   */
  const viewedRef = useRef<number | null>(null);
  useEffect(() => {
    if (cardSerialNo === null || viewedRef.current === cardSerialNo) return;
    viewedRef.current = cardSerialNo;
    void recordCardView(cardSerialNo);
  }, [cardSerialNo]);

  // §4.2 비회원 마스킹 — 카드 식별 정보는 전부 공개, 가격만 "약 N만원대"
  const isAuthenticated = useAuthStore((s) => !!s.accessToken);
  /**
   * 저장된 토큰을 아직 복원하는 중인가 (design 2026-08-27).
   *
   * 회원인지 **모르는 구간**을 '비회원' 으로 단정하면, 로그인해 둔 사람에게 '약 N만원대
   * + 간편로그인' 이 먼저 떴다가 시세로 바뀐다 — 앱이 자기를 못 알아본 것으로 읽힌다.
   *
   * ⚠ 오늘의 dev 에서는 거의 안 뜬다. RootLayout 이 부트스트랩이 settle 할 때까지
   *   라우트를 아예 렌더하지 않아(`authReady`), 이 화면이 마운트될 때는 이미
   *   bootstrapped 다. design 과 같은 방어를 남겨 두는 이유는 그 게이트가 이 화면의
   *   것이 아니라서다 — 저쪽이 바뀌면 여기부터 조용히 틀린다.
   */
  const authBootstrapped = useAuthStore((s) => s.bootstrapped);
  const authPending = !authBootstrapped && !isAuthenticated;
  const { requireAuth } = useRequireAuth();
  // 공유는 이 화면 위에 activity 로 얹는다 (카드 상세 자체가 activity — 2026-08-17).
  const activityNav = useActivityNavigator();
  const [addSheetOpen, setAddSheetOpen] = useState(false);
  /** 신뢰도 배지의 (i) 가 여는 설명 시트 */
  const [confidenceOpen, setConfidenceOpen] = useState(false);
  /** 이력 그래프 기간 (design HISTORY_PERIODS — 7·30·90) */
  const [period, setPeriod] = useState<HistoryPeriod>(30);
  /**
   * FN-017 — 표면(FINISH)은 **가격 축**이다. 카드마다 발행된 표면이 달라 기본값을
   * 응답에서 끌어와야 하는데 훅은 조건부로 부를 수 없으므로, 상태는 null 로 두고
   * 아래에서 이 카드의 첫 표면으로 확정한다.
   */
  const [finishSel, setFinishSel] = useState<string | null>(null);
  /** 담기 CTA 가 제자리에 앉았는가 — 그늘을 걷을지 정한다 (addRest 주석 참조) */
  const [addAtRest, setAddAtRest] = useState(false);
  const addButtonRef = useRef<HTMLDivElement | null>(null);
  const addRestRef = useRef<HTMLDivElement | null>(null);

  // 카드명·공유를 앱바로 승격 (design SC-007 v0.2 — 본문 상단 공간 절약 + 어디서든 공유)
  // ⚠ 라벨은 `share.entry`('공유') 다 — `share.action`('공유하기') 은 공유 화면 안의
  //   실행 버튼·시스템 시트 제목이 쓴다. 앱바 아이콘은 그 화면으로 가는 입구다.
  // ⚠ size 를 주지 않는다(기본). sm 이면 이 앱바에서 혼자 작아 손가락이 빗나간다.
  const shareAction = useMemo(() => (
    cardSerialNo === null ? null : (
      <IconButton
        label={t("share.entry")}
        tooltip={t("share.entry")}
        variant="ghost"
        icon={<Icon name="Share" size="md" />}
        clickAction={() => {
          activityNav.open({ url: `/cards/${cardSerialNo}/share` });
        }}
      />
    )
  ), [cardSerialNo, activityNav, t]);
  useAppBarSlot(detail.data?.name, shareAction);

  /**
   * 담기 — **시트**로 연다 (design SC-012 · 2026-09-03). 종전에는 전체화면 저장 폼을
   * activity 로 띄웠는데, 담는 순간에 물을 것은 수량 하나다(§6.1). 정본 §6.2 가 나열하던
   * 나머지 필드(구매가·구매처·보관장소·메모)는 2026-08-25 에 폐기됐다 — 묻는 곳이 없다.
   */
  const addToCollection = useCallback(() => {
    if (cardSerialNo === null) return;
    setAddSheetOpen(true);
  }, [cardSerialNo]);

  /**
   * §4.3 "원래 하려던 기능이 이어져야 한다".
   * auth 는 이 화면 **위에 오버레이**로 열리므로 페이지는 그대로 마운트돼 있다.
   * 로그인 성공으로 isAuthenticated 가 켜지면 남겨둔 마커를 소비해 액션을 재개한다.
   */
  useEffect(() => {
    if (!isAuthenticated || !rawSerialNo) return;
    if (consumePendingCardAction(rawSerialNo) === "addToCollection") {
      addToCollection();
    }
  }, [isAuthenticated, rawSerialNo, addToCollection]);

  /** 비회원이면 마커를 남기고 로그인 새창을 띄운다 (§4 표: 비회원 컬렉션 저장 불가). */
  const handleAddToCollection = useCallback(() => {
    if (isAuthenticated) {
      addToCollection();
      return;
    }
    if (rawSerialNo) setPendingCardAction(rawSerialNo, "addToCollection");
    // navigate 인텐트를 쓰면 이미 떠 있는 이 화면을 한 번 더 쌓는다 → none + 마커.
    requireAuth({ type: "none" });
  }, [isAuthenticated, rawSerialNo, addToCollection, requireAuth]);

  /* 담기 CTA 가 제자리에 앉았는지 지켜본다 — 앉으면 뒤에 깔아 둔 그늘을 걷는다
     (design 2026-08-27). sticky 버튼은 제자리가 화면 밖일 때만 떠 있으므로, 버튼 바로
     뒤 표식과의 거리가 그리드 간격까지 좁아지면 곧 '내려앉았다' 는 뜻이다.

     ⚠ IntersectionObserver 로 먼저 짰다가 스크롤 측정으로 바꿨다. 이 화면은 뷰포트가
       아니라 .routeMotion 안에서 스크롤되는데, 그 조합에서 관찰자가 콜백을 아예 안 주는
       경우를 실측했다. 스크롤은 확실히 온다.
     ⚠ **화면 아래 경계로 재지 않는다.** 마지막까지 내려도 표식이 뷰포트 밖에 남는
       구간이 있어 영영 참이 되지 않았다 — 버튼과 표식 **사이의 거리**로 판별한다.
     ⚠ rAF 로 한 프레임에 한 번만 잰다 — 스크롤마다 레이아웃을 읽으면 끊긴다. */
  useEffect(() => {
    const sentinel = addRestRef.current;
    if (!sentinel) return;

    let raf = 0;
    const measure = () => {
      raf = 0;
      const button = addButtonRef.current?.querySelector("button");
      if (!button) return;
      const gap =
        sentinel.getBoundingClientRect().top - button.getBoundingClientRect().bottom;
      setAddAtRest(gap <= 32);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };

    measure();
    /* ⚠ **document 에 캡처로 건다.** 스크롤을 실제로 먹는 상자를 조상에서 찾아 붙였더니
       이벤트가 한 번도 안 왔다 (overflow:auto 인 상자와 진짜 스크롤러가 달랐다).
       scroll 은 버블하지 않지만 캡처 단계에서는 document 까지 내려온다. */
    document.addEventListener("scroll", onScroll, { passive: true, capture: true });
    window.addEventListener("resize", onScroll);
    /* ⚠ 이벤트만 믿지 않는다. 스크롤 이벤트가 안 오는 자리를 이미 두 번 만났다.
       0.3초마다 한 번 더 재는 값은 getBoundingClientRect 두 번이라 비용이 사실상 없고,
       대신 '그늘이 영영 안 걷히는' 최악을 막는다. */
    const tick = window.setInterval(measure, 300);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.clearInterval(tick);
      document.removeEventListener("scroll", onScroll, { capture: true });
      window.removeEventListener("resize", onScroll);
    };
    /* ⚠ 의존성은 **카드가 그려졌는지**여야 한다 (undefined → 번호). `cardSerialNo` 는 URL
       에서 파싱한 값이라 로딩 중에도 이미 확정돼 있어, 그걸로 걸면 effect 가 표식이
       아직 없는 첫 렌더에 한 번 돌고 끝난다 — sentinel 이 null 이라 즉시 return 하고
       다시는 안 돈다. 실기기 아닌 브라우저에서 실측으로 잡았다: 거리는 24px 인데
       그늘이 그대로 남아 있었다. */
  }, [card?.cardSerialNo]);

  /**
   * FN-017 표면(FINISH) 축 — 등급별 시세 표를 이 축으로 가른다.
   *
   * ⚠ design 은 이 스위치를 대표 시세 **위**에 두고 화면의 모든 값을 바꿨다. dev 에서는
   *   표면 축을 가진 값이 `variantPrices` 하나뿐이라(대표가는 서버가 '일반판 우선, 없으면
   *   최저가' 로 이미 하나를 고른 값이다) 그 자리에 두면 스위치를 눌러도 위 금액이 안
   *   바뀐다 — 조작할 수 없는 것을 조작할 수 있는 것처럼 보이게 된다. 그래서 실제로
   *   가르는 자리, 등급별 표 위에 둔다.
   * ⚠ 이건 취향이 아니라 **결함 수정**이다. 로컬 실측으로 시세가 있는 34,295장 중
   *   20,806장(61%)이 표면을 둘 이상 갖는데, 표가 표면을 안 보여줘서 '무등급' 행이
   *   값만 다른 채 두세 줄 반복됐다 (예: Emboar — DEFAULT 5,723 / holofoil 4,012 /
   *   reverseHolofoil 6,167, 셋 다 같은 이름의 행).
   */
  /* 이력은 상세와 따로 받는다 — 기간을 바꾸면 이것만 다시 부른다.
     ⚠ 비회원에게는 아예 부르지 않는다(§4.2 — 마스킹 대상은 숫자 전부다). */
  const historyQuery = useCardPriceHistoryQuery(cardSerialNo, period, isAuthenticated);
  const history = historyQuery.data ?? null;
  /* PriceChart 는 숫자 배열만 받는다. 금액이 빠진 날은 선을 끊는 대신 제외한다 —
     관측이 없는 날은 애초에 행이 안 오므로 실제로는 거의 발생하지 않는다. */
  const chartPoints = useMemo(
    () => (history?.points ?? []).map((pt) => pt.amount).filter((v): v is number => v !== null),
    [history],
  );
  /* 관측 종류에 따라 라벨을 고른다 — 위 파일 주석의 69.4% 참조. */
  const isAskSeries = history?.observationKind === "ASK";
  /**
   * 이 카드에 이력이 **있다는 걸 한 번이라도 봤는가.**
   *
   * ⚠ 기간 컨트롤의 수명을 기간과 떼어 놓기 위한 걸쇠다. 컨트롤을 "지금 기간에 그릴
   *   점이 있는가"로 걸면, 관측이 적은 기간(예: 7일 1건)을 고르는 순간 그래프와 함께
   *   **컨트롤 자신이 사라져 되돌아갈 길이 없어진다**(실기기 아닌 브라우저에서 실측 —
   *   7일을 누르면 90일로 못 돌아갔다). 카드가 바뀔 때만 풀린다.
   */
  const [historySeen, setHistorySeen] = useState(false);
  useEffect(() => {
    setHistorySeen(false);
  }, [cardSerialNo]);
  useEffect(() => {
    if (history && history.points.length > 0) setHistorySeen(true);
  }, [history]);

  /**
   * 기본 30일이 비면 **90일로 한 번만 넓힌다.**
   *
   * 수집 주기가 언어마다 달라서 기본값 그대로는 대부분의 한국 카드가 '준비 중' 으로
   * 보인다 — 실측으로 한국판 2,408장 중 **1,511장(62.7%)** 이 30일 창이 비어 있고,
   * 그중 1,163장은 90일 창에는 값이 있다(en 38.7% · ja 0%). 데이터가 있는데 기본값
   * 때문에 안 보이는 것은 "없다" 와 다르다.
   *
   * ⚠ 넓힌 뒤에는 컨트롤이 **90일을 켠 채로 서고** 건수도 '최근 90일' 로 적히므로
   *   사용자가 보고 있는 창을 오해할 여지가 없다.
   * ⚠ 카드당 한 번만 — 사용자가 그 뒤에 30일을 직접 골라 비어 있어도 다시 끌고 가지
   *   않는다(고른 것을 되돌리는 화면은 고장으로 읽힌다).
   */
  const widenedForRef = useRef<number | null>(null);
  useEffect(() => {
    widenedForRef.current = null;
  }, [cardSerialNo]);
  useEffect(() => {
    if (!isAuthenticated || cardSerialNo === null) return;
    if (historyQuery.isPending || historyQuery.isFetching) return;
    if (history !== null || period !== 30) return;
    if (widenedForRef.current === cardSerialNo) return;
    widenedForRef.current = cardSerialNo;
    setPeriod(90);
  }, [
    isAuthenticated,
    cardSerialNo,
    historyQuery.isPending,
    historyQuery.isFetching,
    history,
    period,
  ]);

  const finishes = useMemo(() => {
    const seen: string[] = [];
    for (const v of card?.variantPrices ?? []) {
      if (!seen.includes(v.finish)) seen.push(v.finish);
    }
    return seen;
  }, [card?.variantPrices]);
  /* 표면이 하나뿐인 카드는 축이 아니라 사실이라 선택자를 그리지 않는다 —
     고를 게 없는 선택자는 조작할 수 있다는 착각만 준다. */
  const finish = finishSel && finishes.includes(finishSel) ? finishSel : finishes[0];
  const gradeRows = useMemo(
    () => (card?.variantPrices ?? []).filter((v) => v.finish === finish),
    [card?.variantPrices, finish],
  );

  if (detail.isPending && cardSerialNo !== null) {
    return (
      <Section variant="transparent" padding={4} maxWidth={1120} width="100%" minHeight="100%">
        <VStack gap={4} aria-hidden="true">
          <Skeleton height={200} />
          <Skeleton height={120} />
          <Skeleton height={160} />
        </VStack>
      </Section>
    );
  }

  if (!card) {
    return (
      <Section variant="transparent" padding={4} maxWidth={1120} width="100%" minHeight="100%">
        <VStack gap={3} align="center">
          <Text color="secondary" justify="center">{t("detail.notFound")}</Text>
          {detail.isError && (
            <Button
              variant="secondary"
              label={t("search.retry")}
              clickAction={() => void detail.refetch()}
            />
          )}
        </VStack>
      </Section>
    );
  }

  const listPrice = card.listPrice;
  // §5.4 표시할 시세가 없는 상태 — 숫자를 만들어내지 않는다.
  const hasPrice = listPrice.display !== "NONE" && listPrice.amount !== null;
  const imageUrl = cardImageUrl(card.largeImagePathway ?? card.imagePathway);

  return (
    <Section variant="transparent" padding={4} maxWidth={1120} width="100%" minHeight="100%">
      {/* 설치 배너와 본문 사이 간격용 VStack — Section 은 자식 간격을 주지 않는다. 안쪽 Grid 들여쓰기는 diff 를 줄이려 그대로 뒀다 */}
      <VStack gap={4}>
      <AppInstallBanner />
      {/* 단 수·순서는 CSS 가 정한다(detailGrid — 2단 좌/우, 1단은 order 재배열) */}
      <Grid gap={6} columns={1} className={styles.detailGrid}>
        {/* ── 좌: "이 카드는 무엇인가" ─────────────────────────────────── */}
        <VStack gap={4} className={styles.colLeft}>
          {/* 카드 이미지는 이 화면의 주인공이라 컬럼 폭을 그대로 쓴다 (design 2026-08-19).
              예전엔 폭 132 로 제목 옆에 세워 뒀는데 카드 아트가 너무 작아 무엇을 보는
              화면인지 한눈에 안 들어왔다. 이미지를 위로 올리고 제목·칩을 아래로 내리면
              좁은 컬럼에서도 크게 보여줄 수 있다.
              ⚠ 상한 240px — 넓은 컬럼에서 왼쪽에 붙으면 오른쪽이 통째로 비어 균형이
                무너지므로 가운데 세운다. 아래 제목·칩은 **왼쪽 정렬 그대로다** —
                글은 왼쪽에서 시작해야 읽힌다. */}
          <VStack gap={3} className={styles.blockIdentity} align="center">
            <VStack width="100%" maxWidth={240} gap={0}>
              {/* 카드 실물 비율 63:88.
                  탭하면 SC-008 원본 이미지 뷰어로 (FN-020) — 카드에 인쇄된 HP·기술·
                  약점은 우리가 필드로 들고 있지 않고 이미지 안에만 있다. 확대해서
                  읽는 것이 그 정보로 가는 유일한 길이다. */}
              <button
                type="button"
                className={styles.artworkButton}
                onClick={() => activityNav.open({ url: `/cards/${cardSerialNo}/image` })}
                aria-label={t("image.open", { name: card.name })}
              >
                <AspectRatio ratio={63 / 88}>
                  {imageUrl ? (
                    <img className={styles.artwork} src={imageUrl} alt="" decoding="async" />
                  ) : (
                    <span className={styles.artworkEmpty} aria-hidden="true" />
                  )}
                </AspectRatio>
              </button>
            </VStack>
            <VStack gap={2} justify="start" align="start" width="100%">
              {/* 카드명은 앱바로 승격(AppBarSlot) — 본문 h1 은 접근성 계층용 */}
              <Heading level={3} accessibilityLevel={1} wordBreak="break-all">{card.name}</Heading>
              {/* 세트명 — 카드명 **바로 아래** (design 2026-08-19).
                  명세(FN-015)는 세트를 '카드 상세정보' 9필드에 넣어 뒀는데, 그 블록은
                  기본 닫힘이라 세트로 가는 유일한 길이 접힌 곳에 있었다. 세트는 "이
                  카드가 어디 것인가" 라 이름 바로 다음에 오는 질문이고, 그 답이 곧
                  다음 화면(SC-006)이다.
                  ⚠ 크기는 **감싼 Text** 가 정한다. Link 안에 Text 를 넣으면 Astryx 가
                    그 타입을 body(16px)로 덮어쓴다 — Link 는 주변 글에 맞춰 흐르는
                    인라인 부품이라 그렇다. 그래서 반대로 감싸서 12px 문맥에 놓는다. */}
              {/* ⚠ 10px 은 Astryx 타입에 없다(body 16 · supporting 12). Link 를 Text 로
                    감싸도 안 먹어서(Link 가 자식 타입을 덮어쓴다) <p> 에 직접 준다. */}
              <p className={styles.setLink}>
                <Link
                  href={`/sets/${card.cardset.cardsetSerialNo}`}
                  onClick={(e) => {
                    e.preventDefault();
                    // 카드 상세는 항상 activity 다 — 세트도 그 위에 별도 activity 로
                    // 연다. plain navigate 는 store↔URL 을 어긋나게 한다.
                    activityNav.open({ url: `/sets/${card.cardset.cardsetSerialNo}` });
                  }}
                >
                  <HStack gap={1} align="center">
                    {card.cardset.name}
                    <Icon name="arrowRight" size="sm" />
                  </HStack>
                </Link>
              </p>
              <HStack gap={1} wrap="wrap">
                {/* 번호 없는 인쇄판(일본판 기본 에너지 등)이 실재한다 — 없으면 칩을 안 낸다 */}
                {card.number && <Token size="sm" label={card.number} />}
                {card.rarity && <Token size="sm" color="purple" label={card.rarity} />}
                {/* 미러·마스터볼은 이름·번호·레어도가 기본판과 같아 이 칩이 유일한 구분이다 */}
                {card.variantLabel && (
                  <Token size="sm" color="orange" label={card.variantLabel} />
                )}
                <Token size="sm" color="teal" label={t(`edition.${card.language}`)} />
              </HStack>
            </VStack>
          </VStack>

          {/* 담기 — 1단에서는 CSS 가 화면 하단에 고정한다 (blockAdd).
              §4 표: 비회원은 저장 불가 → 탭하면 간편로그인부터 띄운다.
              ⚠ 여기 있던 '관심' 토글은 걷었다 (2026-08-24). SC-019 v0.4 rev2 가
                "별도 관심/찜 지정 기능 없음 — 컬렉션 담기가 곧 관심 표시" 로 확정해,
                담기 옆에 관심을 따로 두면 같은 뜻의 버튼이 둘이 된다. */}
          {/* ⚠ Card 로 감싸지 않는다 (design). sticky·그늘이 걸리는 것이 상자면 CTA 가
              버튼이 아니라 패널로 읽힌다 — 여기 있던 Card padding 3 + HStack 은 2026-08-24
              에 관심 토글을 걷고 남은 껍데기였다.
              ⚠ Astryx Button 은 ref 를 받지 않는다. 아래 '제자리' 측정에 엘리먼트가
                필요해서 껍데기 하나로 감싼다 — display: contents 라 레이아웃에는 아무
                영향이 없다 (styles.addAnchor). */}
          <div ref={addButtonRef} className={styles.addAnchor}>
            <Button
              variant="primary"
              size="lg"
              className={`${styles.blockAdd} ${addAtRest ? styles.blockAddRest : ""}`}
              label={t("detail.addToCollection")}
              clickAction={handleAddToCollection}
            />
          </div>
          {/* 버튼이 **제자리에 앉았는지** 알려 주는 표식 — 보이지 않는다.
              sticky 버튼은 제자리가 화면 밖일 때만 떠 있다. 이 표식이 버튼에 다가붙었다는
              것은 곧 '내려앉았다' 는 뜻이고, 그때는 뒤에 깔아 둔 그늘을 걷어야 한다 —
              안 그러면 바로 위 줄이 어둠에 잠긴다 (design 2026-08-27). */}
          <div ref={addRestRef} className={styles.addRest} aria-hidden="true" />

          {/* 카드 상세정보 — **펼쳐 둔다** (design 2026-08-20). 카드를 특정하는 참조
              정보라 접어 뒀는데, 본문 맨 끝이라 접어서 아낄 자리가 없고 열어 봐야 아는
              상태가 되레 "여기 뭐가 있나" 를 묻게 했다. 대신 아래 등급별 시세를 접는다. */}
          <VStack gap={2} className={styles.blockFacts}>
            <Heading level={3}>{t("detail.factsTitle")}</Heading>
              {/* ⚠ className 이 곧 라벨 크기다 (.factLabels dt → 12px). 없으면 라벨이
                  값과 같은 16px 로 서서 무엇이 이름이고 무엇이 값인지 안 갈린다. */}
              <MetadataList
                className={styles.factLabels}
                columns="single"
                label={{ position: "start", width: 84 }}
              >
                {/* 세트는 **누를 수 있다** (design 2026-08-19). 9필드 중 유일하게 갈
                    곳이 있는 값이다 — 나머지는 이 카드의 성질이지만 세트는 그 자체로
                    화면(SC-006)이 있다. 그전에는 이름만 적혀 있어 세트로 가려면
                    검색으로 돌아가야 했다.
                    ⚠ 위 카드명 아래 링크와 값이 겹치지만 역할이 다르다 — 하나는
                      '지금 보는 맥락', 하나는 '카드 제원표의 한 줄' 이다. */}
                <MetadataListItem label={t("detail.fact.set")}>
                  <Link
                    href={`/sets/${card.cardset.cardsetSerialNo}`}
                    onClick={(e) => {
                      e.preventDefault();
                      // 카드 상세는 항상 activity 다 — 세트도 그 위에 별도 activity 로
                    // 연다. plain navigate 는 store↔URL 을 어긋나게 한다.
                    activityNav.open({ url: `/sets/${card.cardset.cardsetSerialNo}` });
                    }}
                  >
                    <HStack gap={1} align="center">
                      {card.cardset.name}
                      <Icon name="arrowRight" size="sm" />
                    </HStack>
                  </Link>
                </MetadataListItem>
                <MetadataListItem label={t("detail.fact.series")}>
                  {card.cardset.seriesName ?? "-"}
                </MetadataListItem>
                {/* 유형(POKEMON·TRAINER·ENERGY) — FN-015 v0.2(rev2) 필드. 서버가
                    `CARD_DTL_JSON.supertype` 에서 꺼내 온다(카탈로그 97.3%).
                    ⚠ 값이 없어도 **행은 남긴다** — 아래 아티스트와 같은 규칙이다. */}
                <MetadataListItem label={t("detail.fact.cardType")}>
                  {card.cardType ? t(`set.type.${card.cardType}`, { defaultValue: card.cardType }) : "-"}
                </MetadataListItem>
                <MetadataListItem label={t("detail.fact.number")}>
                  {card.printedNumber ?? card.number ?? "-"}
                </MetadataListItem>
                <MetadataListItem label={t("detail.fact.rarity")}>{card.rarity ?? "-"}</MetadataListItem>
                {/* 에너지는 **포켓몬 카드에만 있다** (design) — 트레이너·에너지 카드는 행
                    자체를 안 낸다. 여기서만 '값 없으면 -' 규칙을 벗어나는 이유는, 그 카드에
                    에너지가 없는 것이 **사실**이지 빠진 값이 아니기 때문이다. '-' 로 적으면
                    "우리가 모른다" 로 읽힌다.
                    ⚠ δ 카드처럼 두 타입인 인쇄판이 실재해 배열이다 (실 데이터 확인). */}
                {(card.energyTypes?.length ?? 0) > 0 && (
                  <MetadataListItem label={t("detail.fact.energy")}>
                    {card.energyTypes!
                      .map((e) => t(`set.energy.${e}`, { defaultValue: e }))
                      .join(" · ")}
                  </MetadataListItem>
                )}
                <MetadataListItem label={t("detail.fact.language")}>
                  {t(`edition.${card.language}`)}
                </MetadataListItem>
                <MetadataListItem label={t("detail.fact.releaseDate")}>
                  {formatDotDate(card.cardset.releasedOn)}
                </MetadataListItem>
                {/* ⚠ 값이 없어도 **행은 남긴다** (design 2026-08-19). 전에는 artist 가
                    없으면 행째 사라져서 카드마다 상세정보가 6개였다가 7개였다가 했다 —
                    명세(FN-015)의 필드 집합이 화면에서 지켜지지 않았고, 실제로
                    "아티스트 정보가 아예 없다" 로 읽혔다. 세트·시리즈·발매일이
                    "-" 로 떨어지는 것과 같은 규칙이다. */}
                <MetadataListItem label={t("detail.fact.artist")}>{card.artist ?? "-"}</MetadataListItem>
              </MetadataList>
          </VStack>
        </VStack>

        {/* ── 우: "얼마인가" (PRD §5) ─────────────────────────────────── */}
        <VStack gap={5} className={styles.colRight}>
          {/* ⚠ '시세' 제목과 에디션 배지를 걷었다 (design 2026-08-19).
              둘 다 바로 아래·위가 이미 말하고 있었다 — 이 블록이 시세라는 건 그 안의
              큰 금액이 말하고, 어느 시장인지는 카드명 아래 토큰이 이미 붙어 있다.
              같은 말을 두 번 하느라 값과 제목 사이에 줄이 하나 더 있었다.
              📌 스크롤을 내리면 그 토큰이 화면 밖으로 나간다 — 시세 블록만 보고 있을 때
                 어느 시장인지 알 수 없어지는 건 design 이 감수한 값이다. */}

          {authPending ? (
            /* 회원인지 아직 모르는 구간 — 무엇도 단정하지 않는다 (design 2026-08-27).
               '간편로그인' 을 먼저 띄웠다가 곧 시세로 바뀌면, 로그인한 사람에게는
               앱이 자기를 못 알아본 것으로 읽힌다. */
            <Card padding={5} className={styles.blockPrice}>
              <VStack gap={2} align="start">
                <Skeleton width="60%" height={32} />
                <Skeleton width="40%" height={16} />
              </VStack>
            </Card>
          ) : !hasPrice ? (
            // §5.4 "산정 불가" — 숫자를 만들어내지 않는다
            <Notice
              className={styles.blockPrice}
              status="info"
              title={t("price.unavailableTitle")}
              description={t("price.unavailableBody")}
            />
          ) : !isAuthenticated ? (
            // §4.2 비회원: 대표가는 "약 N만원대"만, 등급별 가격은 마스킹 + 로그인 유도
            /* ⚠ 인증 복구 중에는 이 카드를 그리지 않는다 — 아래 authPending 분기 */
            <Card padding={5} className={styles.blockPrice}>
              {/* 왼쪽에 값(금액 + 그 단서), 오른쪽에 할 일(로그인) — 한 줄이다
                  (design 2026-08-27). 셋을 세로로 쌓으면 카드가 길어지고, 셋을 한 행에
                  늘어놓으면 안내가 금액과 같은 줄에서 무게를 다툰다.
                  ⚠ wrap — 좁은 기기에서는 버튼이 아래로 내려간다. 금액이 잘리는
                    것보다 접히는 편이 낫다. */}
              <HStack gap={3} align="center" justify="between" width="100%" wrap="wrap">
                <VStack gap={1} align="start">
                  <Heading level={2}>{maskPrice(listPrice.amount ?? 0)}</Heading>
                  <Text type="supporting" color="secondary">{t("guest.maskedGrades")}</Text>
                </VStack>
                {/* secondary — 이 화면의 주 액션은 '컬렉션에 추가'(아래 전폭 primary)다.
                    로그인 유도까지 같은 붉은색이면 둘이 무게를 다툰다. */}
                <Button
                  variant="secondary"
                  label={t("guest.login")}
                  clickAction={() => requireAuth({ type: "none" })}
                />
              </HStack>
            </Card>
          ) : (
            <Card padding={5} className={styles.blockPrice}>
              <VStack gap={4}>
                <VStack gap={2} align="start">
                  <Heading level={2}>{formatAmount(listPrice)}</Heading>

                  {/* 신뢰도·갱신은 **가격 아래**다 (design 2026-08-20).
                      2026-08-19 에 맨 윗줄로 올렸다가 되돌린다. 이 블록의 주인공은
                      금액인데 배지가 그 위에 서니 금액보다 먼저 읽혔다. 신뢰도는
                      "이 값을 믿어도 되나" 라는 **금액에 대한 주석**이라, 금액을 본
                      다음에 오는 게 읽는 순서와 맞는다.
                      ⚠ 표본 안내(REFERENCE_CASE)는 배지 옆에 남긴다. 그 등급이 붙은
                        근거라 떨어뜨리면 무엇 때문인지가 사라진다 (FN-053). */}
                  <HStack gap={2} justify="between" align="center" wrap="wrap" width="100%">
                    {/* 배지와 (i) 는 **붙여 둔다** (2026-08-25). (i) 는 이 배지를
                        설명하는 버튼이라 한 덩어리로 읽혀야 한다 — 사이가 벌어지면
                        옆의 갱신 시각과 어느 쪽에 붙은 것인지 흐려진다.
                        ⚠ IconButton 이 제 안에 이미 여백을 갖고 있어 gap 0 이라도
                          아이콘이 배지에 닿지 않는다. */}
                    <HStack gap={0} align="center" wrap="wrap">
                      <Badge
                        variant={RELIABILITY_BADGE[listPrice.reliability] ?? "neutral"}
                        label={t(`price.reliability.${listPrice.reliability}`, {
                          defaultValue: listPrice.reliability,
                        })}
                      />
                      {/* 신뢰도 배지는 이 앱의 정직성 장치인데(PRD §5.6), 화면 어디에도
                          "보통이 무슨 뜻인지" 가 없으면 배지는 장식으로 읽힌다 — (i) 가
                          그 뜻과 근거를 여는 유일한 문이다 (design 2026-08-25). */}
                      <IconButton
                        label={t("confidence.open")}
                        tooltip={t("confidence.open")}
                        variant="ghost"
                        size="sm"
                        icon={<Icon name="Info" size="sm" />}
                        clickAction={() => setConfidenceOpen(true)}
                      />
                      {/* §5.4 표본이 얇으면 대표 시세라고 부르지 않는다 */}
                      {listPrice.display === "REFERENCE_CASE" && (
                        <Text type="supporting" color="secondary">
                          {t("price.referenceCaseNotice")}
                        </Text>
                      )}
                    </HStack>
                    {/* 24시간이 넘으면 **'오래된 시세'** 를 경고색으로 함께 세운다
                        (SC-007 · FN-016, design 2026-08-28). 기준일만 있으면 읽는 사람이
                        오늘 날짜와 견줘 계산해야 알지만, 이 말은 바로 "이 숫자를 그대로
                        믿지 말라" 로 읽힌다. */}
                    <HStack gap={1} align="center" wrap="wrap">
                      {/* ⚠ 지역 CSS 로 경고색을 칠하지 않는다 — Astryx Text 에는 경고 색이
                          없지만 Badge 에는 있다(variant="warning"). 바로 왼쪽의 신뢰도도
                          Badge 라 같은 부품으로 서고, 색을 우리가 정하지 않아 테마가 바뀌어도
                          따라간다. */}
                      {isStaleQuote(listPrice.refreshedAt) && (
                        <Badge variant="warning" label={t("price.stale")} />
                      )}
                      <Text type="supporting" color="secondary">
                        {formatDotDate(listPrice.trendBaseDate)}
                      </Text>
                    </HStack>
                  </HStack>

                  {/* 등락은 **한 화면에 하나**다 (design priceModel "★ 화면 안에서 숫자가
                      서로 어긋나면 안 된다"). 그래프가 있으면 아래 기간 줄이 그 숫자를
                      맡는다 — 둘을 같이 두면 같은 질문에 답이 둘이 된다.
                      실제로 어긋난다: 서버 추세는 **기준일(관측 기준 30일 전 최근접)** 대비고
                      그래프는 **오늘부터 N일 창**의 첫 점 대비라 창이 다르다
                      (실측 Snivy — 기준일 2026.08.03 대비 30.0% vs 30일 창 18.2%).
                      ⚠ 그래프가 없는 카드에서는 이 줄이 유일한 등락이라 그대로 남긴다. */}
                  {listPrice.trendRate !== null && !historySeen && (
                    <Trend value={listPrice.trendRate} label={t("price.trendSinceBase")} />
                  )}
                </VStack>

                {/* 이력 그래프 — 대표 시세와 **같은 카드 안**이다 (design). 이 금액이
                    어디서 와서 어디로 가는 중인지가 곧 같은 이야기라, 상자를 나누면
                    숫자와 그 궤적이 서로 남처럼 놓인다.
                    ⚠ 근거 값(건수·범위)은 아래 '히스토리' 블록으로 내려갔다 — 여기
                      있던 관측 수 한 줄은 거기서 기간과 함께 다시 말한다. */}
                {historySeen && (
                  <>
                    <Divider />
                    <VStack gap={3}>
                      {/* 좌 = 기간 선택, 우 = 그 기간의 등락 (design 2026-08-25).
                          고르는 것이 먼저고 값은 그 결과다 — "30일을 골랐고, 그동안
                          4.2% 올랐다" 로 왼쪽부터 이어 읽힌다. */}
                      <HStack gap={3} justify="between" align="center" wrap="wrap">
                        <SegmentedControl
                          value={String(period)}
                          onChange={(value) => setPeriod(Number(value) as HistoryPeriod)}
                          label={t("price.periodLabel")}
                          size="sm"
                        >
                          {HISTORY_PERIODS.map((d) => (
                            <SegmentedControlItem
                              key={d}
                              value={String(d)}
                              label={t("price.periodDays", { count: d })}
                            />
                          ))}
                        </SegmentedControl>
                        {history?.changeRate != null && <Trend value={history.changeRate} />}
                      </HStack>

                      {/* ⚠ design 은 매끄러운 추정 선 위에 실제 체결 **점**을 얹었다.
                          그건 일별 데이터가 없어 추세를 지어내던 시절의 장치다 — 지금은
                          선 자체가 관측값이라(pr_prc_bydd 일별 중앙값) 점을 따로 얹을
                          것이 없다. `pr_prc_obs`(관측 원본)가 채워지면 그때 되살린다. */}
                      {chartPoints.length >= 2 ? (
                        <>
                          <PriceChart
                            points={chartPoints}
                            isUp={(history?.changeRate ?? 0) >= 0}
                            ariaLabel={t("price.chartAria", {
                              days: period,
                              change: (history?.changeRate ?? 0).toFixed(1),
                            })}
                          />

                          {/* 축 대신 양 끝 날짜만 — 모바일에서 눈금은 읽히지 않는다 */}
                          <HStack justify="between" align="center">
                            <Text type="supporting" color="secondary">
                              {t("price.daysAgo", { count: period })}
                            </Text>
                            <Text type="supporting" color="secondary">{t("price.today")}</Text>
                          </HStack>
                        </>
                      ) : (
                        /* 점 하나로는 선이 안 그려진다. 빈 자리를 두면 "고장" 으로 읽히므로
                           왜 비었는지 적고, 컨트롤은 남겨 다른 기간으로 갈 수 있게 둔다. */
                        <Text type="supporting" color="secondary">{t("price.periodTooFew")}</Text>
                      )}
                    </VStack>
                  </>
                )}
              </VStack>
            </Card>
          )}

          {/* §4.2 비회원 — 표를 통째로 가리되 **자리는 남긴다**. 종전에는 조건이
              `isAuthenticated &&` 하나뿐이라 비회원에게는 섹션이 아예 사라졌고,
              그래서 로그인하면 등급별 시세가 있다는 사실 자체가 안 보였다. */}
          {!authPending && !isAuthenticated && (
            <VStack gap={2} className={styles.blockGrades}>
              <GuestGateSection
                title={t("detail.priceByGrade")}
                body={t("guest.lockedGrades")}
                onLogin={() => requireAuth({ type: "none" })}
              />
            </VStack>
          )}

          {/* §5.4 감정사·점수가 다르면 합치지 않는다 — 여기는 실 데이터가 있다 */}
          {isAuthenticated && card.variantPrices.length > 0 && (
            <VStack gap={2} className={styles.blockGrades}>
              {/* 접는다 (design 2026-08-20). 감정 등급별 표는 이 화면에서 가장 긴
                  덩어리인데, 대표 시세만 확인하러 온 사람에게는 지금 필요 없다.
                  등급을 따지는 사람만 펼치면 된다. */}
              <Collapsible
                defaultIsOpen={false}
                trigger={<Heading level={3}>{t("detail.priceByGrade")}</Heading>}
              >
              {/* FN-017 표면(FINISH) 축 — 아래 표의 기준을 바꾸므로 표 **위**에 둔다.
                  ⚠ 전폭이다 (design 2026-08-19). 내용 크기로 줄어들면 옆 여백에 묻혀
                    칩처럼 읽혔다.
                  ⚠ design 은 "SegmentedControl 에 폭 prop 이 없다" 며 지역 CSS 로 눌렀는데,
                    **있다** — `layout="fill"` 이 컨테이너를 width:100% flex 로 만들고
                    컨텍스트로 각 칸에 flex:1 까지 준다(SegmentedControl.tsx styles.fill ·
                    SegmentedControlItem 의 isFill). 같은 결과를 라이브러리가 내므로
                    남의 내부 클래스(.astryx-segmented-control-item)를 짚지 않는다. */}
              {finishes.length > 1 && (
                <VStack gap={1} align="start" width="100%">
                  <SegmentedControl
                    value={finish}
                    onChange={(value) => setFinishSel(value)}
                    label={t("price.finishLabel")}
                    size="sm"
                    layout="fill"
                  >
                    {finishes.map((f) => (
                      <SegmentedControlItem key={f} value={f} label={finishLabel(t, f)} />
                    ))}
                  </SegmentedControl>
                  <Text type="supporting" color="secondary">{t("price.finishHint")}</Text>
                </VStack>
              )}
              <Table
                data={gradeRows as unknown as Record<string, unknown>[]}
                idKey={(v) => {
                  const p = v as unknown as VariantPrice;
                  return `${p.finish}-${p.pattern}-${p.grader}-${p.grade}`;
                }}
                density="balanced"
                dividers="rows"
                columns={[
                  {
                    key: "grade",
                    header: t("detail.colGrade"),
                    /*
                     * ⚠ **세 열 모두 minWidth 를 0 으로 둔다** (design 2026-08-20).
                     *
                     * Astryx Table 은 열들의 최소폭에서 표의 min-width 를 계산해
                     * **인라인 style 로 박는다.** 인라인이라 CSS 로는 못 이기고, 그 값이
                     * 상수라 화면이 좁아져도 줄지 않는다 — 좁은 기기에서 표가 화면 밖으로
                     * 뻗었다(pixel(100) 합 340px vs 375 기기 본문 327px).
                     * columnUtils 가 `w.minWidth ?? DEFAULT_MIN_COLUMN_WIDTH` 로 읽으므로
                     * 0 을 명시하면 그대로 0 이 되고(?? 는 0 을 통과시킨다) 인라인 min-width
                     * 자체가 사라져 표가 부모 폭을 따라간다.
                     * ⚠ width 를 아예 빼면 세 열이 균등 분할돼 320px 에서 금액이 잘린다 —
                     *   비율은 있어야 한다.
                     */
                    width: proportional(1, { minWidth: 0 }),
                    renderCell: (v) => {
                      const p = v as unknown as VariantPrice;
                      return (
                        <VStack gap={0} align="start">
                          {/* ⚠ '무등급' 은 "등급이 낮다" 로 오독된다 — PSA 세계의 표준어
                              Raw 를 병기한다 (design). */}
                          <Text weight="semibold">
                            {p.grader === "RAW"
                              ? t("price.gradeRawLabel")
                              : `${p.grader} ${p.grade}`}
                          </Text>
                          {/* 이 행이 곧 위 대표 시세다 (design 2026-08-25). 표 아래 한 줄로
                              적어 봤는데 설명이 값에서 멀어 어느 행인지 다시 짚어야 했다.
                              ⚠ 표에서 **유일하게 하나뿐인** 배지다 — 모든 행에 붙는 순간
                                정보가 아니게 된다. 그래서 '무등급이면' 이 아니라 '금액이
                                대표가와 같으면' 으로 건다: 실 데이터는 전량 UNGRADED 라
                                표면마다 RAW 행이 하나씩 있고, 그중 대표는 하나뿐이다. */}
                          {p.price.amount !== null && p.price.amount === listPrice.amount && (
                            <Badge variant="neutral" label={t("price.gradeRepBadge")} />
                          )}
                        </VStack>
                      );
                    },
                  },
                  {
                    key: "price",
                    header: t("detail.colPrice"),
                    align: "end",
                    // 가장 긴 값('약 567,300원')이 들어가는 열이라 넓게
                    width: proportional(1.4, { minWidth: 0 }),
                    renderCell: (v) => (
                      <Text weight="semibold">
                        {formatAmount((v as unknown as VariantPrice).price)}
                      </Text>
                    ),
                  },
                  {
                    key: "trend",
                    header: t("detail.colTrend"),
                    align: "end",
                    // 한 줄 유지가 필요한 유일한 열이라(CardDetailPage.module.css) 넉넉히
                    width: proportional(1, { minWidth: 0 }),
                    // ⚠ 변수명 t 금지 — useTranslation 의 t 를 가린다
                    renderCell: (v) => {
                      const rate = (v as unknown as VariantPrice).price.trendRate;
                      return rate === null ? (
                        <Text type="supporting" color="secondary">{t("price.trendUnknown")}</Text>
                      ) : (
                        <Trend value={rate} />
                      );
                    },
                  },
                ]}
              />
              </Collapsible>
              {/* 접힌 상태에서는 제목 줄 하나만 남는다 — 구분선이 없으면 아래 블록의
                  제목과 나란히 붙어 목록처럼 읽힌다 (design 2026-08-20). */}
              <Divider />
            </VStack>
          )}

          {/* ── 아직 데이터가 없는 섹션들 ───────────────────────────────
              전부 수집서버 적재 대기다. 자세한 이유는 파일 상단 주석 참조. */}
          {/* ── 근거 — §5.8 "근거를 숨기지 않는다" ────────────────────
              위 대표가·그래프가 어디서 나온 값인지. 창(최근 N일)을 라벨이 아니라 값에
              박아 둔다 — 기간을 안 적으면 그래프의 기간과 다른 창을 말하면서도 같은
              것처럼 읽힌다.
              ⚠ design 의 '출처' 행과 제목 옆 '더보기'(실거래 목록 시트)는 **뺐다** —
                둘 다 `pr_prc_obs`(관측 원본)를 읽는데 그 테이블이 0행이다. 링크만
                두면 눌러서 빈 목록을 만나고, 출처 칸은 영영 비어 있다. */}
          <VStack gap={2} className={styles.blockBasis}>
            {/* ⚠ 인증 복구 중에는 아무것도 단정하지 않는다 — 대표가 블록의 authPending
                과 같은 이유다. '로그인 후 공개' 를 먼저 띄웠다가 곧 그래프로 바뀌면
                로그인한 사람에게는 앱이 자기를 못 알아본 것으로 읽힌다. */}
            {authPending ? null : !isAuthenticated ? (
              <GuestGateSection
                title={t("price.historyTitle")}
                body={t("guest.lockedHistory")}
                onLogin={() => requireAuth({ type: "none" })}
              />
            ) : history && history.observationCount > 0 ? (
              <>
                <Heading level={3}>{t("price.historyTitle")}</Heading>
                {/* 상자를 두른다 (design 2026-08-20) — 성격이 다른 값 여럿을 한
                    덩어리로 묶어 주는 테두리다. */}
                <Card padding={4}>
                  {/* 1열 (design 2026-08-20). 2×2 로 묶었다가 되돌렸다 — 값이
                      '3,500원 ~ 4,800원' 처럼 길어 반 칸에서 두 줄로 접혔다.
                      ⚠ 열 수는 감싼 div 의 CSS 가 정한다. Astryx MetadataList 의
                        columns 는 auto-fit 최소폭을 들고 있어 폭에 따라 제멋대로 접힌다. */}
                  <div className={styles.metaGrid}>
                    <MetadataList className={styles.factLabels} label={{ position: "top" }}>
                      {/* 라벨이 관측 종류를 밝힌다 — 호가뿐인 카드에 '거래 성사'라고
                          쓰면 거짓말이다(한국판 69.4%). */}
                      <MetadataListItem
                        label={isAskSeries ? t("price.field.askCount") : t("price.field.tradeCount")}
                      >
                        {t("price.tradeCountInWindow", {
                          count: history.observationCount,
                          days: history.windowDays,
                        })}{" "}
                        {/* 마지막 관측일은 **건수 옆에 작게** (design 2026-08-25) —
                            '6건 · 최근 90일' 과 같은 이야기의 뒷부분이다.
                            ⚠ 인라인 <span> 이라 줄바꿈·밑선 정렬을 글 흐름이 맡는다.
                              HStack 으로 묶으면 크기가 다른 두 글이 아랫변으로 어긋난다. */}
                        <span className={styles.inlineNote}>
                          {t("price.lastTradeInline", {
                            date: formatDotDate(history.lastObservedOn),
                          })}
                        </span>
                      </MetadataListItem>
                      {history.low !== null && history.high !== null && (
                        <MetadataListItem
                          label={isAskSeries ? t("price.field.askRange") : t("price.field.range")}
                        >
                          {t("price.range", {
                            low: formatKrw(history.low),
                            high: formatKrw(history.high),
                          })}
                        </MetadataListItem>
                      )}
                    </MetadataList>
                  </div>
                </Card>
              </>
            ) : (
              <PendingSection
                title={t("price.historyTitle")}
                badge={t("detail.pendingBadge")}
                body={t("detail.pendingHistory")}
              />
            )}
          </VStack>
          {/* 수입 프리미엄은 한국판에 해당 없음 — 본국이 곧 국내다 (PRD §2.3 USP②) */}
          {card.language !== "ko" && (
            <VStack gap={0} className={styles.blockPremium}>
              <PendingSection
                title={t("premium.title")}
                badge={t("detail.pendingBadge")}
                body={t("detail.pendingPremium")}
              />
            </VStack>
          )}
          {/* ── 지금 거래하면 (§5.3 대표 시세와 섞지 않는다) ──────────────
              라벨을 **사용자 행동 기준**으로 쓴다 (design). '판매호가'는 상대방 기준이라
              "판매호가 = 내가 팔면 받는 값" 으로 정확히 거꾸로 읽힌다 — KREAM·StockX 도
              같은 이유로 뒤집어 쓴다.
              ⚠ design 의 **팔면(BID)·차이(스프레드)는 없다.** 수집 계약의 관측 종류가
                ASK|SOLD 뿐이라 구매희망가라는 축 자체가 안 온다. 두 값 중 하나만 지어내면
                그 '차이'가 곧 거짓이 되므로, 사면 하나만 정직하게 적는다.
              ⚠ 대표가가 이미 호가에서 나온 카드에는 서버가 이걸 안 내려보낸다 — 같은 값을
                큰 금액과 여기에 두 번 쓰게 된다(한국판 69.4%가 그렇다). */}
          <VStack gap={2} className={styles.blockQuotes}>
            {authPending ? null : !isAuthenticated ? (
              <GuestGateSection
                title={t("price.quotesTitle")}
                body={t("guest.lockedQuotes")}
                onLogin={() => requireAuth({ type: "none" })}
              />
            ) : card.askQuote?.amount != null ? (
              <>
                <Heading level={3}>{t("price.quotesTitle")}</Heading>
                <Card padding={4}>
                  <VStack gap={4}>
                    <VStack gap={0} align="start">
                      <Text type="supporting" color="secondary">{t("price.ask")}</Text>
                      <Heading level={3}>{formatKrw(card.askQuote.amount)}</Heading>
                      <Text type="supporting" color="secondary">{t("price.askHint")}</Text>
                    </VStack>
                    <Text type="supporting" color="secondary">{t("price.quotesNotice")}</Text>
                  </VStack>
                </Card>
              </>
            ) : (
              <PendingSection
                title={t("price.quotesTitle")}
                badge={t("detail.pendingBadge")}
                body={t("detail.pendingQuotes")}
              />
            )}
          </VStack>

          {variantUnconfirmed && (
            <VStack gap={0} className={styles.blockTail}>
              <Notice status="warning" title={t("variant.skipNotice")} />
            </VStack>
          )}

        </VStack>
      </Grid>
      </VStack>

      {/* 담기 시트 — 카드 상세 위에 얹힌다. activity 가 아니라 시트라 화면이 그대로
          남고, 담고 나면 제자리다 (design SC-012). */}
      <ConfidenceSheet open={confidenceOpen} onClose={() => setConfidenceOpen(false)} />

      {cardSerialNo !== null && (
        <AddToCollectionSheet
          open={addSheetOpen}
          /* 카드 상세의 담기는 컬렉션에만 들어간다 — 보유는 스캔뿐 (IA rev27) */
          target="collection"
          onClose={() => setAddSheetOpen(false)}
          cardSerialNo={cardSerialNo}
          cardName={detail.data?.name ?? ""}
        />
      )}
    </Section>
  );
}
